import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { services } from '../data';
import { CheckCircle2, ArrowRight, ChevronRight, X, ChevronLeft, ZoomIn } from 'lucide-react';
import Contact from '../components/Contact';

const ServicePage = () => {
  const { slug } = useParams();
  const service = services.find((s) => s.slug === slug);
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    const fetchGallery = async () => {
      setLoading(true);
      try {
        const res = await fetch(`https://api.dev.auxo.dk/public/v2/content/gallerier?term=gallerikategori:${slug}&limit=100`);
        if (res.ok) {
          const data = await res.json();
          const fetchedItems = data.items || [];
          
          // Sorter billeder baseret på "weight" feltet
          const sortedItems = [...fetchedItems].sort((a, b) => {
            const wA = a.weight !== undefined && a.weight !== null ? Number(a.weight) : 0;
            const wB = b.weight !== undefined && b.weight !== null ? Number(b.weight) : 0;
            
            if (wA === 0 && wB === 0) return 0;
            if (wA === 0) return 1; // a har ingen vægt eller 0, så læg den til sidst
            if (wB === 0) return -1; // b har ingen vægt eller 0, så læg den til sidst
            
            return wA - wB; // Stigende rækkefølge (f.eks. 1 før 2)
          });
          
          setGalleryItems(sortedItems);
        }
      } catch (err) {
        console.error('Fejl ved hentning af galleri:', err);
      } finally {
        setLoading(false);
      }
    };

    if (service) {
      fetchGallery();
    }
  }, [slug, service]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (lightboxIndex === null) return;
      if (e.key === 'Escape') {
        closeLightbox();
      } else if (e.key === 'ArrowRight') {
        navigateLightbox(1);
      } else if (e.key === 'ArrowLeft') {
        navigateLightbox(-1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [lightboxIndex, galleryItems]);

  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const openLightbox = (index) => {
    setLightboxIndex(index);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxIndex(null);
    document.body.style.overflow = '';
  };

  const navigateLightbox = (direction) => {
    if (lightboxIndex === null) return;
    let newIndex = lightboxIndex + direction;
    if (newIndex >= galleryItems.length) {
      newIndex = 0;
    } else if (newIndex < 0) {
      newIndex = galleryItems.length - 1;
    }
    setLightboxIndex(newIndex);
  };

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Tjeneste ikke fundet</h1>
          <Link to="/" className="text-orange-600 font-medium hover:underline">Gå tilbage til forsiden</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24">
      {/* Breadcrumbs */}
      <div className="bg-slate-50 py-4 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center text-sm text-slate-500">
            <Link to="/" className="hover:text-orange-600">Forside</Link>
            <ChevronRight className="h-4 w-4 mx-2" />
            <span className="text-slate-900 font-medium">{service.title}</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative py-24 md:py-32 lg:py-40 overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src={service.image} 
            alt={service.title} 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-slate-900/70 mix-blend-multiply"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              {service.title}
            </h1>
            <p className="text-xl text-slate-200 mb-8 leading-relaxed">
              {service.fullDescription}
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
              {service.features.map((feature, index) => (
                <div key={index} className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-orange-500 flex-shrink-0" />
                  <span className="text-slate-100 font-medium">{feature}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-4">
              <a 
                href="#contact" 
                className="bg-orange-600 text-white px-8 py-4 rounded-lg font-bold hover:bg-orange-700 transition-all shadow-lg hover:shadow-orange-600/30 flex items-center gap-2"
              >
                Få et uforpligtende tilbud <ArrowRight className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Reference Gallery Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Referencer: {service.title}</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Se et udpluk af mine tidligere projekter og få inspiration til din egen løsning. 
              Jeg lægger vægt på høj kvalitet og finish i hvert eneste arbejde.
            </p>
          </div>
          
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="aspect-[4/3] bg-slate-200 rounded-xl animate-pulse"></div>
              ))}
            </div>
          ) : galleryItems.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-500 text-lg">Ingen referencer fundet for denne ydelse.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {galleryItems.map((item, index) => (
                <div 
                  key={item.id} 
                  onClick={() => openLightbox(index)}
                  className="group relative overflow-hidden rounded-xl shadow-lg aspect-[4/3] cursor-pointer bg-slate-100"
                >
                  <img 
                    src={item.image?.[0]} 
                    alt={item.title} 
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-slate-950/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-between p-6">
                    <div className="self-end bg-white/20 backdrop-blur-md p-2 rounded-lg text-white">
                      <ZoomIn className="w-5 h-5" />
                    </div>
                    <div>
                      <span className="inline-block bg-orange-600 text-white text-xs font-bold px-2.5 py-1 rounded-full mb-2">
                        {service.title}
                      </span>
                      <h4 className="text-white font-bold text-lg leading-tight line-clamp-2">
                        {item.title}
                      </h4>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Other Services CTA */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            <div>
              <h2 className="text-3xl font-bold mb-2">Interesseret i andre løsninger?</h2>
              <p className="text-slate-400">Jeg udfører alt inden for brolægning og anlægsarbejde.</p>
            </div>
            <div className="flex flex-wrap gap-4">
              {services.filter(s => s.slug !== slug).map((s) => (
                <Link 
                  key={s.slug} 
                  to={`/ydelser/${s.slug}`}
                  className="bg-slate-800 hover:bg-orange-600 text-white px-4 py-2 rounded-md transition-colors text-sm"
                >
                  {s.title}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Contact serviceName={service.title} />

      {/* Lightbox Modal */}
      {lightboxIndex !== null && galleryItems[lightboxIndex] && (
        <div className="fixed inset-0 z-50 bg-slate-950/95 flex flex-col justify-between p-4 md:p-8 select-none">
          {/* Top Bar */}
          <div className="flex justify-between items-center text-white w-full max-w-7xl mx-auto">
            <div className="text-sm font-medium text-slate-400">
              Billede {lightboxIndex + 1} af {galleryItems.length} — {service.title}
            </div>
            <button
              onClick={closeLightbox}
              className="p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors text-white cursor-pointer"
              aria-label="Luk galleri"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Main Content (Image & Navigation) */}
          <div className="relative flex items-center justify-center flex-grow max-w-7xl mx-auto w-full my-4">
            {/* Prev Button */}
            <button
              onClick={(e) => { e.stopPropagation(); navigateLightbox(-1); }}
              className="absolute left-0 md:left-4 z-10 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors cursor-pointer"
              aria-label="Forrige billede"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>

            {/* Image */}
            <div className="max-h-[70vh] md:max-h-[80vh] max-w-full flex items-center justify-center">
              <img
                src={galleryItems[lightboxIndex].image?.[0]}
                alt={galleryItems[lightboxIndex].title}
                className="max-h-[70vh] md:max-h-[80vh] max-w-full object-contain rounded-lg shadow-2xl"
              />
            </div>

            {/* Next Button */}
            <button
              onClick={(e) => { e.stopPropagation(); navigateLightbox(1); }}
              className="absolute right-0 md:right-4 z-10 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors cursor-pointer"
              aria-label="Næste billede"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>

          {/* Bottom Bar (Caption) */}
          <div className="text-center text-white w-full max-w-3xl mx-auto pb-4">
            <h4 className="text-xl font-bold mb-1">
              {galleryItems[lightboxIndex].title}
            </h4>
            <p className="text-sm text-slate-400">
              Udført af Nielsen Brolægning
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServicePage;
